import { TextField } from '@blueprinthq/joy'
import {
  Box,
  Button,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useBreakpointValue,
  VStack
} from '@chakra-ui/react'
import type { FieldProps, FormikHelpers } from 'formik'
import { Field, Form, Formik } from 'formik'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import * as Yup from 'yup'
import styles from './markdown-content.module.css'

interface CalendarConnectModalProps {
  isOpen: boolean
  onClose: () => void
  calendarName: string
  icon: React.ReactNode
  instructions: string
  onSave: (url: string, name?: string) => void
  isLoading?: boolean
}

interface FormValues {
  calendarUrl: string
  optionalName: string
}

const validationSchema = Yup.object().shape({
  calendarUrl: Yup.string()
    .url('Please enter a valid URL')
    .required('Calendar URL is required'),
  optionalName: Yup.string()
})

const CalendarConnectModal: React.FC<CalendarConnectModalProps> = ({
  isOpen,
  onClose,
  calendarName,
  icon,
  instructions,
  onSave,
  isLoading = false
}) => {
  const modalSize = useBreakpointValue({ base: 'full', md: 'xl' })
  const [submitError, setSubmitError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!isOpen) {
      setSubmitError(null)
    }
  }, [isOpen])

  const handleSubmit = async (
    values: FormValues,
    { setFieldError }: FormikHelpers<FormValues>
  ) => {
    try {
      setSubmitError(null)
      await onSave(values.calendarUrl, values.optionalName)
    } catch (error: unknown) {
      setFieldError(
        'calendarUrl',
        'Invalid iCal URL. Please check the URL and try again.'
      )
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={modalSize}>
      <ModalOverlay />
      <ModalContent
        maxW={{ base: '100%', md: '720px' }}
        borderRadius={{ base: '0', md: 'var(--chakra-radii-2xl)' }}
      >
        <Box
          py="16px"
          px="16px"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box w="40px" h="40px" opacity="0" />
          <Heading size="md" fontSize="18px" textAlign="center">
            Connect Calendar
          </Heading>
          <ModalCloseButton
            position="relative"
            top="unset"
            right="unset"
            w="40px"
            h="40px"
          />
        </Box>

        <ModalBody pb={6} px="24px">
          <Formik<FormValues>
            initialValues={{
              calendarUrl: '',
              optionalName: ''
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <VStack align="stretch" spacing={6}>
                  <Box px="16px">
                    <VStack align="stretch" spacing={6}>
                      <HStack spacing={3}>
                        <Box
                          w="48px"
                          h="48px"
                          p="12px 12px 12px 12px"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          borderRadius="8px"
                          border="1px solid"
                          borderColor="rgba(40, 40, 40, 0.05)"
                          bg="#F9F9F9"
                        >
                          {icon}
                        </Box>
                        <Text fontWeight="bold">{calendarName}</Text>
                      </HStack>

                      <Box>
                        <Box className={styles.markdownContent}>
                          <Text fontWeight="bold" pb="8px">
                            Instructions
                          </Text>
                          <ReactMarkdown>{instructions}</ReactMarkdown>
                        </Box>
                      </Box>

                      <Box>
                        <Text fontWeight="bold" mb={2}>
                          Paste your calendar url here
                        </Text>
                        <Field name="calendarUrl">
                          {({ field }: FieldProps) => (
                            // @ts-ignore
                            <TextField
                              {...field}
                              placeholder="Enter your calendar URL"
                              borderColor="#E4E5E6"
                              isInvalid={Boolean(
                                errors.calendarUrl && touched.calendarUrl
                              )}
                              errorText={errors.calendarUrl}
                            />
                          )}
                        </Field>
                      </Box>

                      <Box>
                        <Text fontWeight="bold" mb={2}>
                          Optional name
                        </Text>
                        <Field name="optionalName">
                          {({ field }: FieldProps) => (
                            // @ts-ignore
                            <TextField
                              {...field}
                              placeholder="Enter a name for this calendar"
                              borderColor="#E4E5E6"
                              isInvalid={Boolean(
                                errors.optionalName && touched.optionalName
                              )}
                              errorText={errors.optionalName}
                            />
                          )}
                        </Field>
                      </Box>
                    </VStack>
                  </Box>

                  {submitError && (
                    <Box px="16px">
                      <Text color="error" fontSize="14px">
                        {submitError}
                      </Text>
                    </Box>
                  )}

                  <HStack spacing={2} pt={4}>
                    <Button
                      variant="outline"
                      onClick={onClose}
                      w="50%"
                      borderRadius="8px"
                      h="48px"
                    >
                      Cancel
                    </Button>
                    <Button
                      colorScheme="blue"
                      type="submit"
                      isDisabled={isSubmitting || isLoading}
                      isLoading={isSubmitting || isLoading}
                      w="50%"
                      borderRadius="8px"
                      h="48px"
                    >
                      Save
                    </Button>
                  </HStack>
                </VStack>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default CalendarConnectModal
